import AddIcon from '@mui/icons-material/Add';
import HomeIcon from '@mui/icons-material/Home';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MenuIcon from '@mui/icons-material/Menu';
import MessageIcon from '@mui/icons-material/Message';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PeopleIcon from '@mui/icons-material/People';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import SearchIcon from '@mui/icons-material/Search';
import { AppBar, Badge, Box, IconButton, InputBase, Toolbar, Typography } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import companyLogo from '../assets/header-logo.png';
import { useGetManufacturerByIdQuery } from '../features/manufacturer/manufacturers-api-slice';
import { useGetUnreadCountQuery } from '../features/messaging/messaging-api-slice';
import {
  UserAvatarMenu,
  selectAccessUsers,
  selectCurrentUser,
  selectManageOrders,
} from '../features/user';
import { useAppSelector } from '../store/hooks';
import { UserRole } from '../types';

interface HeaderProps {
  toggleDrawer: () => void;
}

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.black, 0.08),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.black, 0.12),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.common.black,
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: theme.palette.common.black,
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '20ch',
    },
  },
}));

const NavIconButton = styled(IconButton)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  color: theme.palette.common.black,
  padding: theme.spacing(1),
  overflow: 'visible',
  '& .MuiTypography-root': {
    fontSize: '0.7rem',
    marginTop: theme.spacing(0.5),
  },
}));

const Header = ({ toggleDrawer }: HeaderProps) => {
  const userManagementVisible = useAppSelector(selectAccessUsers);
  const currentUser = useAppSelector(selectCurrentUser);
  const canManageOrders = useAppSelector(selectManageOrders);
  const navigate = useNavigate();

  const { data: manufacturer } = useGetManufacturerByIdQuery(
    currentUser?.manufacturerId?.toString() ?? '',
    {
      skip: !currentUser?.manufacturerId,
    }
  );

  const { data: unreadCount = 0 } = useGetUnreadCountQuery(undefined, {
    pollingInterval: 30000,
  });

  const logoUrl = manufacturer?.logoUrl || companyLogo;

  const handleHomeClick = () => {
    // Navigate to dashboard for Supervisor/CSR/Admin, home for others
    if (
      currentUser?.role === UserRole.SUPERVISOR ||
      currentUser?.role === UserRole.CSR ||
      currentUser?.role === UserRole.ADMINISTRATOR
    ) {
      navigate('/dashboard');
    } else {
      navigate('/');
    }
  };

  return (
    <AppBar position="static">
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img src={logoUrl} alt="Company Logo" style={{ height: '40px', marginRight: '16px' }} />
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Search>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <StyledInputBase placeholder="Search…" inputProps={{ 'aria-label': 'search' }} />
        </Search>

        {/* Navigation Icon Buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-evenly',
            width: '100%',
            maxWidth: '600px',
          }}
        >
          <NavIconButton aria-label="home" onClick={handleHomeClick}>
            <HomeIcon />
            <Typography variant="caption">Home</Typography>
          </NavIconButton>

          {canManageOrders && (
            <NavIconButton
              aria-label="create order"
              onClick={() => navigate('/order-management?create=true')}
            >
              <AddIcon />
              <Typography variant="caption">Create Order</Typography>
            </NavIconButton>
          )}

          <NavIconButton aria-label="order list" onClick={() => navigate('/order-management')}>
            <ListAltIcon />
            <Typography variant="caption">Order List</Typography>
          </NavIconButton>

          <NavIconButton aria-label="messaging" onClick={() => navigate('/messaging')}>
            <Badge
              badgeContent={unreadCount}
              color="primary"
              sx={{
                '& .MuiBadge-badge': {
                  top: 2,
                  right: -2,
                },
              }}
            >
              <MessageIcon />
            </Badge>
            <Typography variant="caption">Messaging</Typography>
          </NavIconButton>

          <NavIconButton aria-label="notifications">
            <NotificationsIcon />
            <Typography variant="caption">Notifications</Typography>
          </NavIconButton>

          {currentUser?.role !== UserRole.DEALER && (
            <NavIconButton aria-label="price" onClick={() => navigate('/bundle-cost-management')}>
              <PriceCheckIcon />
              <Typography variant="caption">Price</Typography>
            </NavIconButton>
          )}

          {/* User Management Button - Only visible for administrators */}
          {userManagementVisible && (
            <NavIconButton
              aria-label="user management"
              onClick={() => navigate('/user-management')}
            >
              <PeopleIcon />
              <Typography variant="caption">Users</Typography>
            </NavIconButton>
          )}
        </Box>

        {/* User Avatar Menu */}
        <UserAvatarMenu />

        <IconButton
          sx={{ color: 'black', ml: 1 }}
          aria-label="open drawer"
          edge="end"
          onClick={toggleDrawer}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
